import { NavigationType, type createBrowserRouter, type RouterNavigateOptions, type To } from 'react-router';

type Router = ReturnType<typeof createBrowserRouter>;
export type NavDirection = 'forward' | 'back' | 'tab' | 'fade';

/**
 * Глубина экрана на телефоне — как стек экранов в мобильном Slack:
 * 0 — экраны нижних вкладок (Главная, Личные, Активность, Вы), 1 — беседа и разделы,
 * 2 — тред, сведения или профиль поверх беседы и вложенные страницы.
 */
export function screenDepth(pathname: string, search: string): number {
  const rest = pathname.replace(/^\/w\/[^/]+/, '').replace(/\/$/, '');
  const params = new URLSearchParams(search);
  const overlay = params.has('thread') || params.has('details') || params.has('profile') ? 1 : 0;
  if (rest === '' || rest === '/dms' || rest === '/activity') return overlay;
  if (rest === '/settings/profile' || rest === '/settings') return (params.has('edit') ? 1 : 0) + overlay;
  if (/^\/apps\/[^/]+/.test(rest)) return 2 + overlay;
  return 1 + overlay;
}

/** Куда «едет» экран: глубже — справа, назад — вправо, между вкладками — мягкая смена. */
export function navDirection(from: { pathname: string; search: string }, to: { pathname: string; search: string }): NavDirection {
  const a = screenDepth(from.pathname, from.search);
  const b = screenDepth(to.pathname, to.search);
  if (b > a) return 'forward';
  if (b < a) return 'back';
  return a === 0 && from.pathname !== to.pathname ? 'tab' : 'fade';
}

const PHONE = '(max-width: 767px)';
const REDUCED = '(prefers-reduced-motion: reduce)';

function matches(query: string): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

/**
 * Переходы экранов на телефоне (View Transitions API): каждая навигация просит у роутера анимацию,
 * а направление ставим на <html data-nav> до снимка старого экрана — стили в global.css.
 * На компьютере, без поддержки браузера и при «уменьшить движение» — мгновенно, как раньше.
 */
export function enableScreenTransitions(router: Router): Router {
  if (typeof document === 'undefined' || typeof document.startViewTransition !== 'function') return router;
  const original = router.navigate.bind(router);
  function navigate(to: number): Promise<void>;
  function navigate(to: To | null, opts?: RouterNavigateOptions): Promise<void>;
  function navigate(to: number | To | null, opts?: RouterNavigateOptions): Promise<void> {
    if (typeof to === 'number') return original(to);
    const animate = matches(PHONE) && !matches(REDUCED);
    return original(to, animate ? { ...opts, viewTransition: true } : opts);
  }
  router.navigate = navigate;
  // Подписка раньше RouterProvider: направление выставляется до того, как он начнёт переход.
  // Кнопка «Назад» браузера и жест смахивания: у iOS и Android своя анимация — здесь только мягкая смена.
  router.subscribe((state, opts) => {
    const vt = opts.viewTransitionOpts;
    if (vt === undefined) return;
    document.documentElement.dataset.nav = state.historyAction === NavigationType.Pop ? 'fade' : navDirection(vt.currentLocation, vt.nextLocation);
  });
  return router;
}
